/**
 * Subscription Context
 * Provides a centralized SubscriptionClient with key-based registry, retained data,
 * and optional trade price slice. One wire subscription per (channel, params); hooks are consumers.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { SubscriptionClient, InfoClient } from "../sdk";
import { ConnectionState, StreamMessage } from "../sdk/types";
import type { MultiplexStreamSubscription } from "../sdk/types/api";
import type { MultiplexStreamType } from "../sdk/types/api";
import { DEFAULT_WS_URL } from "../constants";
import { parseSubscriptionKey } from "./subscriptionKey";
import type { LastPriceData, MarkPriceData, RecentTradeDos } from "../sdk/types/data";
import { toNumberFromSatoshi } from "../sdk/utils/math";
import { normalizeTimestamp, normalizeProtobufTimestamp } from "../sdk/utils/time";
import { transformRawTradesToRecentTradeDos } from "../sdk/types/data";
import type { MultiFeedLastPriceData, MultiFeedMarkPriceData } from "../sdk/types/api";

/** Registry entry: refCount, callbacks, unsub fns, optional last data. */
interface RegistryEntry {
  refCount: number;
  callbacks: Set<(data: unknown) => void>;
  unsubFns: (() => void)[];
  lastData?: unknown;
}

/** Trade price slice state exposed to consumers. */
export interface TradePriceSlice {
  lastPrice: LastPriceData | null;
  markPrice: MarkPriceData | null;
  displayPrice: number | null;
  displayTimestamp: number | null;
  recentTrades: RecentTradeDos[];
  loading: boolean;
  error: string | null;
  isConnected: boolean;
  priceChange: "up" | "down" | null;
}

interface SubscriptionContextType {
  client: SubscriptionClient;
  isEnabled: boolean;
  isConnected: boolean;
  connectionState: ConnectionState;
  error: Error | null;
  toggleEnabled: () => void;
  setEnabled: (enabled: boolean) => void;
  clearError: () => void;
  // Key-based API (Option A)
  subscribe: (key: string, callback: (data: unknown) => void) => void;
  unsubscribe: (key: string, callback: (data: unknown) => void) => void;
  getRetainedDataByKey: (key: string) => unknown | null;
  // Trade price slice
  tradePriceMarketId: string | null;
  tradePriceData: TradePriceSlice | null;
  setTradePriceMarketId: (marketId: string | null) => void;
  refreshTradePrice: () => Promise<void>;
  // Optional base URL for trade price HTTP init (getMarketTrades)
  baseURL: string | undefined;
  setBaseURL: (url: string | undefined) => void;
  // Retained data: latest data per sub-channel (legacy)
  getRetainedData: (subChannel: string) => StreamMessage | null;
  getAllRetainedData: () => Map<string, StreamMessage>;
  clearRetainedData: (subChannel?: string) => void;
  // Legacy subscription methods (deprecated; use subscribe(key, callback))
  subscribeMultiFeedWithRetention: (
    marketId: string,
    symbol: string,
    callback: (data: StreamMessage) => void,
    interval?: string,
  ) => void;
  subscribeSubChannelWithRetention: (
    marketId: string,
    symbol: string,
    subChannel: string,
    callback: (data: unknown) => void,
    interval?: string,
  ) => void;
  unsubscribeMultiFeed: (
    marketId: string,
    symbol: string,
    callback?: (data: StreamMessage) => void,
  ) => void;
  unsubscribeSubChannel: (
    marketId: string,
    subChannel: string,
    callback?: (data: unknown) => void,
  ) => void;
  // Configuration
  wsURL: string;
  setWsURL: (url: string) => void;
  authToken: string | undefined;
  setAuthToken: (token: string | undefined) => void;
  walletAddress: string | undefined;
  setWalletAddress: (address: string | undefined) => void;
  deviceID: string | undefined;
  setDeviceID: (deviceId: string | undefined) => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

interface SubscriptionProviderProps {
  children: ReactNode;
  defaultWsURL?: string;
  defaultBaseURL?: string;
  defaultAuthToken?: string;
  defaultWalletAddress?: string;
  defaultDeviceID?: string;
}

const emptyTradePriceSlice: TradePriceSlice = {
  lastPrice: null,
  markPrice: null,
  displayPrice: null,
  displayTimestamp: null,
  recentTrades: [],
  loading: true,
  error: null,
  isConnected: false,
  priceChange: null,
};

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
  defaultWsURL = DEFAULT_WS_URL,
  defaultBaseURL,
  defaultAuthToken,
  defaultWalletAddress,
  defaultDeviceID,
}) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Disconnected,
  );
  const [error, setError] = useState<Error | null>(null);
  const [wsURL, setWsURL] = useState(defaultWsURL);
  const [baseURL, setBaseURLState] = useState<string | undefined>(defaultBaseURL);
  const [authToken, setAuthToken] = useState<string | undefined>(
    defaultAuthToken,
  );
  const [walletAddress, setWalletAddress] = useState<string | undefined>(
    defaultWalletAddress,
  );
  const [deviceID, setDeviceID] = useState<string | undefined>(
    defaultDeviceID,
  );
  // Trade price slice: state value is tradePriceMarketId; setter is setTradePriceMarketIdState.
  const [tradePriceMarketId, setTradePriceMarketIdState] = useState<string | null>(null);
  const [tradePriceData, setTradePriceData] = useState<TradePriceSlice | null>(null);

  const setBaseURL = useCallback((url: string | undefined) => {
    setBaseURLState(url);
  }, []);

  // Initialize client immediately - it should never be null
  const clientRef = useRef<SubscriptionClient>(
    new SubscriptionClient({
      url: defaultWsURL,
      authToken: defaultAuthToken,
      walletAddress: defaultWalletAddress,
      deviceID: defaultDeviceID,
    })
  );
  const previousWsURLRef = useRef<string>(defaultWsURL);
  const unsubscribeStateChangeRef = useRef<(() => void) | null>(null);
  const retainedDataRef = useRef<Map<string, StreamMessage>>(new Map());
  const retainedDataByKeyRef = useRef<Map<string, unknown>>(new Map());
  const wrappedCallbacksRef = useRef<Map<(data: unknown) => void, (data: unknown) => void>>(new Map());
  /** Legacy multi-feed: one entry per marketId so multiple markets do not share one stream. */
  const multiFeedByMarketRef = useRef<
    Map<string, { subs: MultiplexStreamSubscription[]; unsubFns: (() => void)[]; callback: (data: StreamMessage) => void }>
  >(new Map());
  /** Legacy sub-channel: key = subChannel:${marketId} so multiple markets do not share one stream. */
  const subChannelSubsRef = useRef<
    Map<string, { unsubFn: () => void; sub: MultiplexStreamSubscription }>
  >(new Map());

  // Key-based registry: key -> { refCount, callbacks, unsubFns }
  const registryRef = useRef<Map<string, RegistryEntry>>(new Map());
  const resubscribePendingRef = useRef<boolean>(false);

  // Create/recreate subscription client when URL or credentials change
  useEffect(() => {
    // Check if we need to recreate the client (URL or credentials changed)
    const urlChanged = previousWsURLRef.current !== wsURL;
    const needsRecreation = urlChanged;

    if (needsRecreation) {
      // Clean up existing client
      if (unsubscribeStateChangeRef.current) {
        unsubscribeStateChangeRef.current();
        unsubscribeStateChangeRef.current = null;
      }
      clientRef.current.disconnect();

      // Clear retained data when reconnecting to a new URL
      retainedDataRef.current.clear();

      // Create new client with current settings
      const client = new SubscriptionClient({
        url: wsURL,
        authToken,
        walletAddress,
        deviceID,
      });
      clientRef.current = client;
      previousWsURLRef.current = wsURL;
    }

    // Handle connection state based on isEnabled
    if (isEnabled) {
      // Track connection state if not already tracking
      if (!unsubscribeStateChangeRef.current) {
        const unsubscribeStateChange = clientRef.current.onStateChange((state) => {
          setConnectionState(state);
          setIsConnected(state === ConnectionState.Connected);
          // Clear error on successful connection
          if (state === ConnectionState.Connected) {
            setError(null);
            resubscribePendingRef.current = true;
          }
        });
        unsubscribeStateChangeRef.current = unsubscribeStateChange;
      }

      // Connect if not already connected
      if (!clientRef.current.isConnected()) {
        clientRef.current
          .connect()
          .then(() => {
            console.debug("[SubscriptionContext] Connected to WebSocket");
            setError(null);
          })
          .catch((err) => {
            const error =
              err instanceof Error ? err : new Error(String(err));
            console.error("[SubscriptionContext] Failed to connect:", error);
            setError(error);
          });
      }
    } else {
      // Disconnect when disabled, but keep client instance
      if (unsubscribeStateChangeRef.current) {
        unsubscribeStateChangeRef.current();
        unsubscribeStateChangeRef.current = null;
      }
      if (clientRef.current.isConnected()) {
        clientRef.current.disconnect();
      }
      // Clear retained data when disabled
      retainedDataRef.current.clear();
      // Clear wrapped callbacks tracking
      wrappedCallbacksRef.current.clear();
      setConnectionState(ConnectionState.Disconnected);
      setIsConnected(false);
      setError(null);
    }

    return () => {
      // Cleanup on unmount or dependency change
      if (unsubscribeStateChangeRef.current) {
        unsubscribeStateChangeRef.current();
        unsubscribeStateChangeRef.current = null;
      }
      // Disconnect but keep client instance (never set to null)
      if (clientRef.current.isConnected()) {
        clientRef.current.disconnect();
      }
    };
  }, [isEnabled, wsURL, authToken, walletAddress, deviceID]);

  // Update client auth when credentials change
  useEffect(() => {
    if (clientRef.current && isEnabled) {
      if (authToken && walletAddress && deviceID) {
        clientRef.current.setAuthToken(authToken, walletAddress, deviceID);
      }
    }
  }, [isEnabled, authToken, walletAddress, deviceID]);

  /**
   * Perform wire subscription for a given key. Called when first consumer subscribes or on reconnect.
   */
  const doSubscribe = useCallback(
    (key: string) => {
      const client = clientRef.current;
      if (!client || !client.isConnected() || !client.isReadyForSubscriptions()) {
        return;
      }
      const parsed = parseSubscriptionKey(key);
      if (!parsed) {
        console.warn("[SubscriptionContext] Invalid subscription key:", key);
        return;
      }
      const entry = registryRef.current.get(key);
      if (!entry || entry.refCount < 1) return;

      const notifyCallbacks = (data: unknown) => {
        retainedDataByKeyRef.current.set(key, data);
        entry.callbacks.forEach((cb) => {
          try {
            cb(data);
          } catch (err) {
            console.error("[SubscriptionContext] Callback error for key", key, err);
          }
        });
      };

      if (parsed.type === "multiplex" && parsed.streamType && parsed.marketId != null) {
        const mi = Number(parsed.marketId);
        const marketIndex = Number.isNaN(mi) ? 0 : mi;
        const streamType = parsed.streamType as MultiplexStreamType;
        const sub: MultiplexStreamSubscription = {
          stream_type: streamType,
          filter:
            streamType === "orderbook"
              ? { marketIndices: [marketIndex], includeSnapshot: true }
              : { marketIndices: [marketIndex] },
        };
        const multiplex = client.getMultiplexStreamClient();
        multiplex
          .subscribeToChannel()
          .then(() => multiplex.subscribeStreams([sub]))
          .then(() => {
            const unsubFn = multiplex.onStreamData(streamType, (r) => {
              if (r.data != null) notifyCallbacks(r.data);
            });
            entry.unsubFns.push(unsubFn);
          })
          .catch((err) => {
            const e = err instanceof Error ? err : new Error(String(err));
            console.error("[SubscriptionContext] Multiplex subscribe error:", key, e);
            setError(e);
          });
        return;
      }

      if (parsed.type === "buckets") {
        try {
          const unsubFn = (): void => {
            client.unsubscribeBuckets(sharedCallback);
          };
          const sharedCallback = (data: unknown) => notifyCallbacks(data);
          client.subscribeBuckets(sharedCallback);
          entry.unsubFns.push(unsubFn);
        } catch (err) {
          const e = err instanceof Error ? err : new Error(String(err));
          console.error("[SubscriptionContext] Buckets subscribe error:", e);
          setError(e);
        }
        return;
      }

      if (parsed.type === "multiFeed" && parsed.subChannel && parsed.marketId != null) {
        try {
          const params: Record<string, unknown> = {};
          if (parsed.subChannel === "kline" && parsed.interval) {
            params.interval = parsed.interval;
            params.symbol = "BTC/USDC";
          }
          const sharedCallback = (data: unknown) => notifyCallbacks(data);
          client.subscribeMultiFeedSubChannel(
            parsed.subChannel,
            parsed.marketId,
            sharedCallback,
            Object.keys(params).length > 0 ? params : undefined,
          );
          const unsubFn = (): void => {
            client.unsubscribeMultiFeedSubChannel(parsed.subChannel!, sharedCallback);
          };
          entry.unsubFns.push(unsubFn);
        } catch (err) {
          const e = err instanceof Error ? err : new Error(String(err));
          console.error("[SubscriptionContext] MultiFeed subscribe error:", key, e);
          setError(e);
        }
      }
    },
    [],
  );

  /**
   * Subscribe by key. One wire subscription per key; ref-counted.
   */
  const subscribe = useCallback((key: string, callback: (data: unknown) => void) => {
    if (!key || typeof callback !== "function") return;
    let entry = registryRef.current.get(key);
    if (!entry) {
      entry = {
        refCount: 0,
        callbacks: new Set(),
        unsubFns: [],
      };
      registryRef.current.set(key, entry);
    }
    const isFirst = entry.refCount === 0;
    entry.refCount += 1;
    entry.callbacks.add(callback);
    if (isFirst && clientRef.current?.isConnected() && clientRef.current?.isReadyForSubscriptions()) {
      doSubscribe(key);
    }
  }, [doSubscribe]);

  /**
   * Unsubscribe callback; when refCount hits 0, tear down wire subscription.
   */
  const unsubscribe = useCallback((key: string, callback: (data: unknown) => void) => {
    const entry = registryRef.current.get(key);
    if (!entry) return;
    entry.callbacks.delete(callback);
    entry.refCount -= 1;
    if (entry.refCount <= 0) {
      entry.unsubFns.forEach((fn) => {
        try {
          fn();
        } catch (e) {
          console.warn("[SubscriptionContext] Unsubscribe fn error:", e);
        }
      });
      entry.unsubFns.length = 0;
      registryRef.current.delete(key);
      retainedDataByKeyRef.current.delete(key);
    }
  }, []);

  const getRetainedDataByKey = useCallback((key: string): unknown | null => {
    return retainedDataByKeyRef.current.get(key) ?? null;
  }, []);

  /**
   * Re-subscribe all active keys (on reconnect).
   */
  const resubscribeAllKeys = useCallback(() => {
    if (!clientRef.current?.isConnected() || !clientRef.current?.isReadyForSubscriptions()) {
      resubscribePendingRef.current = true;
      return;
    }
    resubscribePendingRef.current = false;
    const keys = Array.from(registryRef.current.keys());
    keys.forEach((key) => {
      const entry = registryRef.current.get(key);
      if (entry && entry.refCount > 0) {
        entry.unsubFns.forEach((fn) => {
          try {
            fn();
          } catch (e) {
            console.warn("[SubscriptionContext] Resubscribe cleanup:", e);
          }
        });
        entry.unsubFns.length = 0;
        doSubscribe(key);
      }
    });
  }, [doSubscribe]);

  // On connect, re-subscribe all keys in registry
  useEffect(() => {
    if (isConnected && isEnabled && resubscribePendingRef.current) {
      const t = setTimeout(() => {
        resubscribeAllKeys();
      }, 100);
      return () => clearTimeout(t);
    }
  }, [isConnected, isEnabled, resubscribeAllKeys]);

  /**
   * Legacy: subscribe to multiplex streams (priceFeed, markPrice, orderbook, trades) for one market.
   * Key includes marketId so multiple markets do not share one stream.
   * Prefer subscribe(key, callback) for new code (e.g. subscribe("priceFeed:"+marketId, cb)).
   */
  const subscribeMultiFeedWithRetention = useCallback(
    (
      marketId: string,
      _symbol: string,
      callback: (data: StreamMessage) => void,
      _interval?: string,
    ) => {
      if (!clientRef.current) {
        console.warn("[SubscriptionContext] Cannot subscribe: client not initialized");
        return;
      }
      if (!marketId || typeof marketId !== "string") {
        console.warn("[SubscriptionContext] Invalid marketId:", marketId);
        return;
      }
      if (multiFeedByMarketRef.current.has(marketId)) {
        return;
      }

      const marketIndex = Number(marketId);
      const mi = Number.isNaN(marketIndex) ? 0 : marketIndex;
      const subs: MultiplexStreamSubscription[] = [
        { stream_type: "priceFeed", filter: { marketIndices: [mi] } },
        { stream_type: "markPrice", filter: { marketIndices: [mi] } },
        { stream_type: "orderbook", filter: { marketIndices: [mi], includeSnapshot: true } },
        { stream_type: "trades", filter: { marketIndices: [mi] } },
      ];

      const multiplex = clientRef.current.getMultiplexStreamClient();
      const unsubFns: (() => void)[] = [];
      const pushMessage = (streamType: string, data: unknown) => {
        const message: StreamMessage = {
          type: "data",
          channel: "multiplex",
          subChannel: streamType,
          data,
        };
        retainedDataRef.current.set(streamType, message);
        const entry = multiFeedByMarketRef.current.get(marketId);
        entry?.callback(message);
      };

      multiFeedByMarketRef.current.set(marketId, { subs, unsubFns, callback });

      multiplex
        .subscribeToChannel()
        .then(() => multiplex.subscribeStreams(subs))
        .then(() => {
          unsubFns.push(
            multiplex.onStreamData("priceFeed", (r) => {
              if (r.data != null) pushMessage("priceFeed", r.data);
            }),
          );
          unsubFns.push(
            multiplex.onStreamData("markPrice", (r) => {
              if (r.data != null) pushMessage("markPrice", r.data);
            }),
          );
          unsubFns.push(
            multiplex.onStreamData("orderbook", (r) => {
              if (r.data != null) pushMessage("orderbook", r.data);
            }),
          );
          unsubFns.push(
            multiplex.onStreamData("trades", (r) => {
              if (r.data != null) pushMessage("trades", r.data);
            }),
          );
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error(String(err));
          console.error("[SubscriptionContext] Multiplex subscription error:", error);
          setError(error);
          multiFeedByMarketRef.current.delete(marketId);
        });
    },
    [],
  );

  /**
   * Legacy: subscribe to a single multiplex sub-channel for one market.
   * Internal key is subChannel:${marketId} so multiple markets do not share one stream.
   * Prefer subscribe(key, callback) for new code (e.g. subscribe("priceFeed:"+marketId, cb)).
   */
  const subscribeSubChannelWithRetention = useCallback(
    (
      marketId: string,
      symbol: string,
      subChannel: string,
      callback: (data: unknown) => void,
      _interval?: string,
    ) => {
      if (!clientRef.current) {
        console.warn("[SubscriptionContext] Cannot subscribe: client not initialized");
        return;
      }
      if (!marketId || typeof marketId !== "string") {
        console.warn("[SubscriptionContext] Invalid marketId:", marketId);
        return;
      }
      if (symbol != null && symbol !== "" && (typeof symbol !== "string" || !symbol.includes("/"))) {
        console.warn("[SubscriptionContext] Invalid symbol (must be empty or BASE/QUOTE):", symbol);
        return;
      }
      if (!subChannel || typeof subChannel !== "string") {
        console.warn("[SubscriptionContext] Invalid subChannel:", subChannel);
        return;
      }
      const key = `subChannel:${marketId}:${subChannel}`;
      if (subChannelSubsRef.current.has(key)) {
        return;
      }

      const marketIndex = Number(marketId);
      const mi = Number.isNaN(marketIndex) ? 0 : marketIndex;
      const sub: MultiplexStreamSubscription = {
        stream_type: subChannel as import("../sdk/types/api").MultiplexStreamType,
        filter: subChannel === "orderbook" ? { marketIndices: [mi], includeSnapshot: true } : { marketIndices: [mi] },
      };
      const multiplex = clientRef.current.getMultiplexStreamClient();

      multiplex
        .subscribeToChannel()
        .then(() => multiplex.subscribeStreams([sub]))
        .then(() => {
          const unsubFn = multiplex.onStreamData(
            subChannel as import("../sdk/types/api").MultiplexStreamType,
            (r) => {
              if (r.data == null) return;
              const message: StreamMessage = {
                type: "data",
                channel: "multiplex",
                subChannel,
                data: r.data,
              };
              retainedDataRef.current.set(key, message);
              retainedDataRef.current.set(subChannel, message);
              callback(r.data);
            },
          );
          subChannelSubsRef.current.set(key, { unsubFn, sub });
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error(String(err));
          console.error("[SubscriptionContext] Sub-channel subscription error:", error);
          setError(error);
        });
    },
    [],
  );

  /**
   * Legacy: unsubscribe from multi-feed for the given marketId.
   * Prefer unsubscribe(key, callback) for new code.
   */
  const unsubscribeMultiFeed = useCallback(
    (marketId: string, _symbol: string, _callback?: (data: StreamMessage) => void) => {
      if (!clientRef.current) return;
      const entry = multiFeedByMarketRef.current.get(marketId);
      if (!entry) return;
      try {
        entry.unsubFns.forEach((fn) => fn());
        if (entry.subs.length > 0) {
          clientRef.current.getMultiplexStreamClient().unsubscribeStreams(entry.subs);
        }
        multiFeedByMarketRef.current.delete(marketId);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        console.error("[SubscriptionContext] Unsubscribe error:", error);
        setError(error);
        multiFeedByMarketRef.current.delete(marketId);
      }
    },
    [],
  );

  /**
   * Legacy: unsubscribe from sub-channel for the given marketId.
   * Prefer unsubscribe(key, callback) for new code.
   */
  const unsubscribeSubChannel = useCallback((marketId: string, subChannel: string, _callback?: (data: unknown) => void) => {
    if (!clientRef.current) return;
    const key = `subChannel:${marketId}:${subChannel}`;
    const entry = subChannelSubsRef.current.get(key);
    if (!entry) return;
    try {
      entry.unsubFn();
      clientRef.current.getMultiplexStreamClient().unsubscribeStreams([entry.sub]);
      subChannelSubsRef.current.delete(key);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error("[SubscriptionContext] Unsubscribe error:", error);
      setError(error);
    }
  }, []);

  const toggleEnabled = useCallback(() => {
    setIsEnabled((prev) => !prev);
  }, []);

  const setEnabled = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
  }, []);

  /**
   * Get retained data for a specific sub-channel
   * @param subChannel - Sub-channel name
   * @returns Retained StreamMessage or null if not found
   */
  const getRetainedData = useCallback((subChannel: string) => {
    return retainedDataRef.current.get(subChannel) || null;
  }, []);

  /**
   * Get all retained data
   * @returns A copy of the Map containing all retained data
   */
  const getAllRetainedData = useCallback(() => {
    return new Map(retainedDataRef.current);
  }, []);

  /**
   * Clear retained data for a specific sub-channel or all if no subChannel provided
   * @param subChannel - Optional sub-channel name. If not provided, clears all retained data
   */
  const clearRetainedData = useCallback((subChannel?: string) => {
    if (subChannel) {
      retainedDataRef.current.delete(subChannel);
    } else {
      retainedDataRef.current.clear();
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const setTradePriceMarketId = useCallback((marketId: string | null) => {
    setTradePriceMarketIdState(marketId);
  }, []);

  const tradePricePrevMarketRef = useRef<string | null>(null);
  const tradePriceMarketIdRef = useRef<string | null>(null);
  tradePriceMarketIdRef.current = tradePriceMarketId;
  const tradePriceLastPriceCbRef = useRef<((data: unknown) => void) | null>(null);
  const tradePriceMarkPriceCbRef = useRef<((data: unknown) => void) | null>(null);

  const refreshTradePrice = useCallback(async () => {
    const marketId = tradePriceMarketIdRef.current;
    if (!marketId || !baseURL) return;
    try {
      const info = new InfoClient({ baseURL });
      const trades = await info.getMarketTrades(marketId, 100);
      if (trades && Array.isArray(trades) && trades.length > 0) {
        const formatted = transformRawTradesToRecentTradeDos(trades);
        const sorted = [...trades].sort((a, b) => normalizeTimestamp(b.timestamp) - normalizeTimestamp(a.timestamp));
        const mostRecent = sorted[0];
        setTradePriceData((prev) => {
          if (!prev) return prev;
          const price = toNumberFromSatoshi(mostRecent.price);
          return {
            ...prev,
            recentTrades: formatted,
            lastPrice: {
              marketIndex: marketId,
              price,
              quantity: toNumberFromSatoshi(mostRecent.quantity),
              timestamp: normalizeTimestamp(mostRecent.timestamp),
              isBuyerMaker: false,
              tradeId: mostRecent.tradeId || "",
            },
            displayPrice: price,
            displayTimestamp: normalizeTimestamp(mostRecent.timestamp),
            loading: false,
          };
        });
      }
    } catch (err) {
      setTradePriceData((prev) =>
        prev ? { ...prev, error: err instanceof Error ? err.message : "Failed to refresh", loading: false } : null,
      );
    }
  }, [baseURL]);

  useEffect(() => {
    const marketId = tradePriceMarketId;
    if (!marketId) {
      if (tradePricePrevMarketRef.current) {
        const prev = tradePricePrevMarketRef.current;
        const lp = tradePriceLastPriceCbRef.current;
        const mp = tradePriceMarkPriceCbRef.current;
        if (lp) unsubscribe(`lastPrice:${prev}`, lp);
        if (mp) unsubscribe(`markPrice:${prev}`, mp);
        tradePriceLastPriceCbRef.current = null;
        tradePriceMarkPriceCbRef.current = null;
      }
      setTradePriceData(null);
      tradePricePrevMarketRef.current = null;
      return;
    }

    const prevMarket = tradePricePrevMarketRef.current;
    if (prevMarket && prevMarket !== marketId) {
      const lp = tradePriceLastPriceCbRef.current;
      const mp = tradePriceMarkPriceCbRef.current;
      if (lp) unsubscribe(`lastPrice:${prevMarket}`, lp);
      if (mp) unsubscribe(`markPrice:${prevMarket}`, mp);
      tradePriceLastPriceCbRef.current = null;
      tradePriceMarkPriceCbRef.current = null;
    }

    setTradePriceData((prev) => ({
      ...emptyTradePriceSlice,
      loading: true,
      error: null,
      isConnected,
    }));

    const lastPriceKey = `lastPrice:${marketId}`;
    const markPriceKey = `markPrice:${marketId}`;

    const onLastPrice = (data: unknown) => {
      const d = data as MultiFeedLastPriceData | null;
      if (!d?.price) return;
      const price = toNumberFromSatoshi(d.price);
      const quantity = toNumberFromSatoshi(d.quantity ?? "0");
      const timestamp = normalizeProtobufTimestamp(d.timestamp);
      setTradePriceData((prev) => {
        if (!prev) return prev;
        const lastPrice = {
          marketIndex: marketId,
          price,
          quantity,
          timestamp,
          isBuyerMaker: d.isBuyerMaker ?? false,
          tradeId: d.tradeId ?? "",
        };
        let priceChange: "up" | "down" | null = null;
        if (prev.lastPrice && typeof prev.lastPrice.price === "number") {
          if (price > prev.lastPrice.price) priceChange = "up";
          else if (price < prev.lastPrice.price) priceChange = "down";
        }
        const newRecent = !d.derived
          ? [{ time: timestamp, size: quantity, price, isBuyerMaker: d.isBuyerMaker ?? false }, ...prev.recentTrades].slice(0, 100)
          : prev.recentTrades;
        const displayPrice = lastPrice.price;
        const displayTimestamp = timestamp;
        return {
          ...prev,
          lastPrice,
          recentTrades: newRecent,
          displayPrice,
          displayTimestamp,
          priceChange,
          loading: false,
        };
      });
    };

    const onMarkPrice = (data: unknown) => {
      const d = data as MultiFeedMarkPriceData | null;
      if (d?.markPrice == null) return;
      const markPriceNum = toNumberFromSatoshi(d.markPrice);
      const timestamp = normalizeProtobufTimestamp(d.timestamp);
      setTradePriceData((prev) => {
        if (!prev) return prev;
        const markPrice = { symbol: d.symbol || "", markPrice: markPriceNum, timestamp };
        const displayPrice = prev.lastPrice?.timestamp && prev.lastPrice.timestamp >= timestamp ? prev.displayPrice : markPriceNum;
        const displayTimestamp = prev.lastPrice?.timestamp && prev.lastPrice.timestamp >= timestamp ? (prev.displayTimestamp ?? 0) : timestamp;
        return {
          ...prev,
          markPrice,
          displayPrice: displayPrice ?? markPriceNum,
          displayTimestamp: displayTimestamp ?? timestamp,
          loading: false,
        };
      });
    };

    tradePriceLastPriceCbRef.current = onLastPrice;
    tradePriceMarkPriceCbRef.current = onMarkPrice;
    subscribe(lastPriceKey, onLastPrice);
    subscribe(markPriceKey, onMarkPrice);

    if (baseURL) {
      const info = new InfoClient({ baseURL });
      info
        .getMarketTrades(marketId, 100)
        .then((trades) => {
          if (trades && Array.isArray(trades) && trades.length > 0) {
            const formatted = transformRawTradesToRecentTradeDos(trades);
            const sorted = [...trades].sort((a, b) => normalizeTimestamp(b.timestamp) - normalizeTimestamp(a.timestamp));
            const mostRecent = sorted[0];
            setTradePriceData((prev) => {
              if (!prev) return prev;
              const price = toNumberFromSatoshi(mostRecent.price);
              return {
                ...prev,
                recentTrades: formatted,
                lastPrice: {
                  marketIndex: marketId,
                  price,
                  quantity: toNumberFromSatoshi(mostRecent.quantity),
                  timestamp: normalizeTimestamp(mostRecent.timestamp),
                  isBuyerMaker: false,
                  tradeId: mostRecent.tradeId || "",
                },
                displayPrice: price,
                displayTimestamp: normalizeTimestamp(mostRecent.timestamp),
                loading: false,
              };
            });
          } else {
            setTradePriceData((p) => (p ? { ...p, loading: false } : null));
          }
        })
        .catch((err) => {
          setTradePriceData((p) =>
            p ? { ...p, error: err instanceof Error ? err.message : "Failed to load trades", loading: false } : null,
          );
        });
    } else {
      setTradePriceData((p) => (p ? { ...p, loading: false } : null));
    }

    tradePricePrevMarketRef.current = marketId;
    return () => {
      unsubscribe(lastPriceKey, onLastPrice);
      unsubscribe(markPriceKey, onMarkPrice);
      tradePriceLastPriceCbRef.current = null;
      tradePriceMarkPriceCbRef.current = null;
      if (tradePricePrevMarketRef.current === marketId) tradePricePrevMarketRef.current = null;
    };
  }, [tradePriceMarketId, baseURL, isConnected, subscribe, unsubscribe]);

  // Expose client with retained data wrappers
  const contextValue = useMemo(
    () => ({
      client: clientRef.current,
      isEnabled,
      isConnected,
      connectionState,
      error,
      toggleEnabled,
      setEnabled,
      clearError,
      subscribe,
      unsubscribe,
      getRetainedDataByKey,
      tradePriceMarketId: tradePriceMarketId,
      tradePriceData,
      setTradePriceMarketId,
      refreshTradePrice,
      baseURL,
      setBaseURL,
      getRetainedData,
      getAllRetainedData,
      clearRetainedData,
      wsURL,
      setWsURL,
      authToken,
      setAuthToken,
      walletAddress,
      setWalletAddress,
      deviceID,
      setDeviceID,
      subscribeMultiFeedWithRetention,
      subscribeSubChannelWithRetention,
      unsubscribeMultiFeed,
      unsubscribeSubChannel,
    }),
    [
      isEnabled,
      isConnected,
      connectionState,
      error,
      toggleEnabled,
      setEnabled,
      clearError,
      subscribe,
      unsubscribe,
      getRetainedDataByKey,
      tradePriceMarketId,
      tradePriceData,
      setTradePriceMarketId,
      refreshTradePrice,
      baseURL,
      setBaseURL,
      getRetainedData,
      getAllRetainedData,
      clearRetainedData,
      wsURL,
      authToken,
      walletAddress,
      deviceID,
      subscribeMultiFeedWithRetention,
      subscribeSubChannelWithRetention,
      unsubscribeMultiFeed,
      unsubscribeSubChannel,
    ],
  );

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Hook to access subscription context
 * Must be used within SubscriptionProvider
 */
export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within SubscriptionProvider",
    );
  }
  return context;
};
